/**
 * ExecutiveCharts — cards 2255 + 3871.
 *
 * The executive charts, drawn from the SAME `/admin/analytics/executive` payload that feeds the
 * KPI row. That shared source is the point: the cards and the charts cannot disagree about a
 * period, a grain or a population, because there is only one of each. The alternative (a second
 * endpoint for the series) passes the same eyeball test today and drifts silently the first time
 * either population rule changes.
 *
 * ⛔ NOTHING HERE COMPUTES MONEY, AND NOTHING HERE NAMES A POPULATION. Every figure arrives per
 *   bucket from `buildTimeseries`, and every population label arrives as the API's own
 *   `population` block. The pure model that shapes both lives in `web/utils/executiveChartModel.js`
 *   — outside this file because jest on this tree cannot import `.jsx` (no transform, no babel,
 *   `.jsx` not in `moduleFileExtensions`), so helpers kept here would be untestable.
 *
 * THE CONTROL BAR IS A SIBLING (./ExecutiveChartControls) — this file is charts only.
 *
 * ONE HEADLINE CARD, TWO VIEWS (3871). `Revenue & Profit ⇄ Members` swaps the same card's body;
 *   the money view renders EXACTLY the series in `MONEY_CHART_SERIES` and keeps the full
 *   gross → reserve → net → expense → profit chain in the tooltip. Total Members keeps its own
 *   card, rendered only in the members view where it belongs.
 *
 * CARD 4310 — FULL WIDTH, FOUR SERIES, AND THE SCOPE NOTE MOVES IN HERE.
 *   · The grid is ONE track (see the stylesheet), so the timeline gets the whole page instead of
 *     the 33% it had as one child of a three-track grid.
 *   · Three dollar series on the left axis and `newMembers` on its OWN right axis — a count and
 *     dollars sharing one scale flattens the count onto zero, which is indistinguishable on
 *     screen from a genuine zero.
 *   · `ExecutiveFirstOrderValueCaption` renders INSIDE this card rather than as a banner above
 *     the KPI tiles it does not scope (card 3658 measured that mismatch). It is imported, not
 *     re-implemented, so its copy is still the API's `firstOrderValue` block verbatim.
 *
 * PERCENT CONVENTIONS ARE NOT UNIFORM IN THE PAYLOAD (LEARNINGS:5374) — `profitMargin` is a
 *   fraction, `newMembersGrowthPct` and `memberGrowthPct` are already percents. This file imports
 *   `formatGrowthPct` from ExecutiveKpiRow rather than writing a third formatter, so every growth
 *   chip on the page is the same function of the same field and they cannot disagree.
 */

import { memo, useEffect, useMemo, useState } from 'react';
import {
  AreaChart, Area, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import {
  formatCount, formatMoney, formatMarginPct, formatGrowthPct, deriveGrowthDirection,
  ExecutiveFirstOrderValueCaption, EXECUTIVE_KPI_DEFS
} from './ExecutiveKpiRow';
import {
  /* ⚠️ `MONEY_SERIES` was imported here and never referenced — a PRE-EXISTING `no-unused-vars`
     error at HEAD (proved by linting `git show HEAD:` of this file), invisible until now because
     `npm run lint` runs `eslint api/ web/`, and `eslint <dir>` walks only `.js` and silently skips
     every `.jsx` in the repo. The component reads `MONEY_CHART_SERIES`, which already contains it.
     Removed by card 5338 because this card's own criterion requires this file to lint clean. */
  DEFAULT_EXECUTIVE_VIEW, resolveOfferedView, MEMBERS_SERIES,
  MONEY_CHART_SERIES, TOOLTIP_LINES, PARTIAL_BUCKET_MARKER,
  deriveChartPoints, deriveUnresolved, derivePartialCount, resolvePopulationLabel,
  deriveMembersSummary, deriveRevenueIntegrity, resolveExecutivePanelState,
  deriveChartAxisConfig, formatCompactMoney
} from '../../utils/executiveChartModel';
import styles from './ExecutiveCharts.module.css';

/** Pure. `2026-07-01T00:00:00.000Z` -> `Jul 1, 2026`. */
const formatWindowDate = (iso) => {
  if (!iso) return null;
  const t = new Date(iso);
  return Number.isNaN(t.getTime())
    ? null
    : t.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
};

/**
 * The preceding-EQUAL comparison window, named on every chart, plus the population this chart is
 * actually drawing. `all_time` has no earlier period to compare against, so it says so rather
 * than rendering a fabricated 0%.
 */
const ComparisonCaption = ({ comparisonWindow, growth = undefined, population = null }) => {
  const start = formatWindowDate(comparisonWindow?.start);
  const end = formatWindowDate(comparisonWindow?.end);
  const growthText = growth === undefined ? null : formatGrowthPct(growth);
  const direction = growth === undefined ? null : deriveGrowthDirection(growth);
  const toneClass = direction === 'up' ? styles.up : direction === 'down' ? styles.down : styles.flat;
  /* Server-supplied, never typed here — see resolvePopulationLabel. */
  const scope = population
    ? <span className={styles.population} data-testid="executive-chart-population">{population}</span>
    : null;

  if (!start || !end) {
    return (
      <p className={styles.caption} data-testid="executive-chart-comparison" data-has-comparison="false">
        {scope}
        <span>No preceding window — all time has nothing earlier to compare against</span>
      </p>
    );
  }

  return (
    <p className={styles.caption} data-testid="executive-chart-comparison" data-has-comparison="true">
      {scope}
      {growthText && (
        <span className={`${styles.growth} ${toneClass}`} data-testid="executive-chart-growth" data-direction={direction}>
          {growthText}
        </span>
      )}
      <span>vs preceding equal window {start} – {end}</span>
    </p>
  );
};

const AXIS = { fontSize: 10, fill: '#9ca3af' };
const GRID = '#f0f0f0';

/* CARD 5338 — the phone breakpoint, as ONE string used by the hook below. It is deliberately the
   same 480px the stylesheet's narrow tier uses, so the chart's chrome and its container shrink at
   the same width instead of at two nearby ones. */
const NARROW_CHART_QUERY = '(max-width: 480px)';
const MOBILE_QUERY = '(max-width: 768px)';

/**
 * CARD 5338 — is the viewport a phone? Subscribes to the media query rather than reading
 * `innerWidth`, so a rotation or a resize re-renders the chart with fewer axis labels.
 *
 * ⛔ THE DECISION IS NOT MADE HERE. This hook answers exactly one boolean; every number derived
 *   from it lives in `deriveChartAxisConfig` in the model, where jest can execute it. That split is
 *   the criterion, not a preference — a `.jsx` on this tree is unreachable from the suite.
 *
 * 🪤 GUARDED FOR ABSENCE ON PURPOSE. `matchMedia` is missing under SSR and under jest's `node`
 *   environment; an unguarded call throws at module scope and takes the whole dashboard with it.
 *   Absent ⇒ `false` ⇒ the wide config ⇒ today's shipped behaviour, which is the safe default.
 */
const useMediaQuery = (query) => {
  const readMatch = () =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches
      : false;

  const [isNarrow, setIsNarrow] = useState(readMatch);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mql = window.matchMedia(query);
    const handleChange = (event) => setIsNarrow(event.matches);
    /* Re-read on mount: the query can already have flipped between first render and this effect. */
    setIsNarrow(mql.matches);
    mql.addEventListener('change', handleChange);
    return () => mql.removeEventListener('change', handleChange);
  }, [query]);

  return isNarrow;
};

/**
 * One chart card. `count` is published to the DOM so a test can read point counts without OCR.
 * `footer` is what renders BELOW the comparison caption — card 4310 puts the scope caption there,
 * inside the card that draws the population it describes.
 *
 * 🪤 ⛔ DO NOT WRITE THE CAPTION'S RENDERED WORDS INTO A COMMENT HERE, not even to explain this.
 *   A criterion pins a tree-wide grep of that copy over `web/` at zero, to prove the words come
 *   from the API and are not retyped in the client — and a comment QUOTING them is a hit like any
 *   other. Card 3658 broke exactly this guard, with a comment describing the rule it broke.
 */
const ChartCard = ({
  testid, title, count, comparisonWindow, growth, population, chip, footer = null,
  skeleton = false, children
}) => (
  <section className={styles.card} data-testid={testid} data-role="chart-card" data-point-count={count}>
    <header className={styles.head}>
      <h3 className={styles.title}>{title}</h3>
      <span className={styles.headMeta}>
        {chip}
        <span className={styles.points} data-testid="executive-chart-points">{count} pts</span>
      </span>
    </header>
    {/* The plot box is a FIXED height in the stylesheet, so swapping the drawing for a
        placeholder INSIDE it cannot move the card. That is what makes the skeleton free:
        the height was never a function of what is drawn. */}
    <div className={styles.plot}>
      {skeleton ? (
        <div className={styles.plotSkeleton} data-testid={`${testid}-skeleton`} aria-hidden="true" />
      ) : count === 0 ? (
        <p className={styles.empty}>No activity in this period</p>
      ) : children}
    </div>
    <ComparisonCaption comparisonWindow={comparisonWindow} growth={growth} population={population} />
    {footer}
  </section>
);

/**
 * The note that explains the axis mark on an incomplete bucket. Renders nothing when the window
 * happens to align with every bucket boundary — an explanation of a mark nobody can see is noise.
 */
const PartialBucketNote = ({ count }) => (count > 0 ? (
  <p className={styles.partialNote} data-testid="executive-partial-note" data-partial-count={count}>
    {PARTIAL_BUCKET_MARKER} {count === 1 ? 'One bucket is' : `${count} buckets are`} only partly
    inside this window — shown in full rather than dropped, so no real money is hidden.
  </p>
) : null);

const countTooltip = (value, name) => [formatCount(value), name];

/**
 * The money tooltip renders the WHOLE accounting chain, not just the two drawn series — that is
 * what lets the headline stay two lines without dropping an accounting line. A `null` figure
 * renders EMPTY_VALUE through `formatMoney`, ⛔ never `$0.00`.
 */
const MoneyTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload ?? {};
  return (
    <div className={styles.tooltip} data-testid="executive-money-tooltip">
      <p className={styles.tooltipHead}>{label}</p>
      {TOOLTIP_LINES.map(({ dataKey, name }) => (
        <p key={dataKey} className={styles.tooltipRow} data-key={dataKey}>
          <span>{name}</span>
          <span>{formatMoney(point[dataKey])}</span>
        </p>
      ))}
      {/* The fourth drawn series is a COUNT, so it is formatted as one and kept outside the
          accounting chain above — a member count rendered as `$4.00` would be a plain lie. */}
      <p className={styles.tooltipRow} data-key={MEMBERS_SERIES.dataKey}>
        <span>{MEMBERS_SERIES.name}</span>
        <span>{formatCount(point[MEMBERS_SERIES.dataKey])}</span>
      </p>
      {point.isPartial === true && (
        <p className={styles.tooltipPartial} data-key="isPartial">
          {PARTIAL_BUCKET_MARKER} Partly outside this window
        </p>
      )}
    </div>
  );
};

/**
 * The margin tile's OWN label and formatter, taken from the shared defs rather than restated here.
 * The note beside the figure and the KPI tile above it are therefore the same function of the same
 * field and cannot disagree — and `profitMargin` is a FRACTION, which is exactly the convention
 * `formatMarginPct` already encodes (LEARNINGS:5374).
 */
const MARGIN_DEF = Object.freeze({ label: 'Profit Margin', format: formatMarginPct });

/**
 * Card 4314 — what the published margin actually divides by, stated beside the figure itself.
 *
 * 🪤 ⛔ DO NOT WRITE THE DENOMINATOR'S RENDERED WORDS INTO A COMMENT OR A STRING HERE. A
 *   criterion pins a tree-wide grep of the rendered phrase over `web/` at zero, to prove those
 *   words arrive from the API's `marginDenominator` and are not retyped in the client — and a
 *   comment quoting them is a hit like any other. `ChartCard` above carries the same warning for
 *   the same reason; card 3658 broke exactly this guard.
 *
 * Renders nothing when the payload published no denominator, rather than a sentence that trails
 * off into an empty divisor.
 */
const MarginDenominatorNote = ({ integrity, profitMargin }) => {
  if (!integrity?.denominatorLabel) return null;
  const { zeroRevenueOrderCount: n } = integrity;
  return (
    <p
      className={styles.integrityNote}
      data-testid="executive-margin-denominator"
      data-denominator={integrity.denominator}
    >
      <span className={styles.integrityFigure} data-testid="executive-margin-figure">
        {MARGIN_DEF.label} {MARGIN_DEF.format(profitMargin)}
      </span>
      {' = profit ÷ '}
      {integrity.denominatorLabel}
      {integrity.hasZeroRevenueOrders ? (
        <span data-testid="executive-margin-ex-zero" data-count={n}>
          {integrity.estimatedRevenueOrderCount > 0
            ? ` · ${formatCount(integrity.estimatedRevenueOrderCount)} zero-recorded-revenue orders use ${formatMoney(integrity.estimatedRevenue)} estimated site-price revenue`
            : ` · excludes ${formatCount(n)} ${n === 1 ? 'order' : 'orders'} with zero recorded revenue`}
        </span>
      ) : null}
      {integrity.reserveInformationalOnly ? (
        <span data-testid="executive-reserve-information">
          {` · reserve shown for reference only: ${formatMoney(integrity.reserveInformational)}`}
        </span>
      ) : null}
    </p>
  );
};

/** The panel's id, shared by `aria-controls` and the panel itself so they cannot drift apart. */
const NOTES_PANEL_ID = 'executive-notes-panel';

/**
 * Card 5134 — the explanatory notes below the headline plot collapse behind ONE disclosure.
 *
 * This component owns VISIBILITY AND NOTHING ELSE. The notes stay mounted and keep being fed
 * exactly as before, so no copy, no payload field and no testid moves; only whether the panel
 * is displayed changes.
 *
 * ⛔ NOT a `<details>`. A closed `<details>` still lays its panel out in some engines, so
 *   "collapsed" would be a claim the DOM does not back. The `hidden` attribute is what makes
 *   the panel report zero height and a null `offsetParent`.
 * ⛔ NOT hover. Hover does not exist at 768px, and ExecutiveKpiRow states that constraint in
 *   source for the very caption this panel contains: a note nobody can reach is not a note.
 *   A native <button> answers click, Enter and Space without a keyboard handler of its own.
 */
const ExecutiveNotesDisclosure = ({ children }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className={styles.notes}>
      <button
        type="button"
        className={styles.notesToggle}
        data-testid="executive-notes-toggle"
        aria-expanded={open ? 'true' : 'false'}
        aria-controls={NOTES_PANEL_ID}
        onClick={() => setOpen((wasOpen) => !wasOpen)}
      >
        {/* A drawn glyph, not an emoji — STANDARDS.md forbids emojis in UI. */}
        <span className={styles.notesGlyph} aria-hidden="true">i</span>
        About these numbers
      </button>
      <div
        id={NOTES_PANEL_ID}
        className={styles.notesPanel}
        data-testid={NOTES_PANEL_ID}
        hidden={!open}
      >
        {children}
      </div>
    </div>
  );
};

const ExecutiveCharts = ({
  metrics = null,
  loading = false,
  view = DEFAULT_EXECUTIVE_VIEW
}) => {
  /* The grain the SERVER echoed, not the one we asked for — labelling week buckets as days is
     invisible on screen, so the label must follow the response. */
  const grain = metrics?.granularity;
  const activeView = resolveOfferedView(view);

  /* The DRAWN window, needed to tell a complete bucket from one the window only clips. */
  const window = metrics?.window ?? null;

  const points = useMemo(
    () => deriveChartPoints(
      metrics?.timeseries,
      metrics?.totalMembersGenuine ?? metrics?.totalMembers,
      grain,
      window
    ),
    [metrics?.timeseries, metrics?.totalMembersGenuine, metrics?.totalMembers, grain, window]
  );
  const unresolved = useMemo(() => deriveUnresolved(points, activeView), [points, activeView]);
  const members = useMemo(() => deriveMembersSummary(metrics, points), [metrics, points]);
  const partialCount = useMemo(() => derivePartialCount(points), [points]);
  const integrity = useMemo(() => deriveRevenueIntegrity(metrics), [metrics]);

  /* CARD 5338 — one boolean from the viewport, every NUMBER from the pure model. */
  const isNarrow = useMediaQuery(NARROW_CHART_QUERY);
  const isMobile = useMediaQuery(MOBILE_QUERY);
  const axis = useMemo(() => deriveChartAxisConfig(isNarrow), [isNarrow]);

  /* Card 5133 — an early `return` used to stand here and hand back a DIFFERENT subtree while a
     period was in flight. React therefore unmounted this whole region, the page collapsed to the
     height of one line of text and then jumped back when the data landed. The tree below is now
     the ONLY tree this component returns, so the `executive-charts` node survives the switch;
     `data-loading` still carries the state for a machine reader, and only the drawing swaps. */
  const isSkeleton = resolveExecutivePanelState({ loading, metrics }) === 'skeleton';

  const comparisonWindow = metrics?.comparisonWindow ?? null;
  const count = points.length;
  const isMoney = activeView === 'money';
  const population = resolvePopulationLabel(metrics?.population, activeView);

  /* Unknown money must be visibly unknown. The chip counts BOTH shapes the payload distinguishes:
     a stored line we could not resolve, and an order carrying no economics row at all. */
  const chip = unresolved.total > 0 ? (
    <span
      className={styles.unresolvedChip}
      data-testid="executive-unresolved"
      data-count={unresolved.total}
      data-unresolved-lines={unresolved.unresolvedLines}
      data-orders-without-economics={unresolved.ordersWithoutEconomics}
      title={`${unresolved.unresolvedLines} unresolved lines, ${unresolved.ordersWithoutEconomics} orders without economics`}
    >
      {unresolved.total} unresolved
    </span>
  ) : null;

  /* Card 4314 — the outliers BEHIND the margin, disclosed inside the card that draws it. An
     order that shipped medication for $0 is a real loss and stays in every total; this only
     says how many there were and what they cost.
     ⛔ Rendered ONLY when there are such orders — a `0` chip would assert an outlier that does
     not exist, which is a different and worse error than saying nothing. */
  const zeroRevenueChip = isMoney && integrity?.hasZeroRevenueOrders ? (
    <span
      className={styles.zeroRevenueChip}
      data-testid="executive-zero-revenue"
      data-count={integrity.zeroRevenueOrderCount}
      data-expense={integrity.zeroRevenueExpense}
      title={`${formatCount(integrity.zeroRevenueOrderCount)} orders had zero recorded revenue; ${formatCount(integrity.estimatedRevenueOrderCount)} use estimated site-price revenue totaling ${formatMoney(integrity.estimatedRevenue)}`}
    >
      {formatCount(integrity.estimatedRevenueOrderCount)} estimated-revenue {integrity.estimatedRevenueOrderCount === 1 ? 'order' : 'orders'}
    </span>
  ) : null;

  const chips = (chip || zeroRevenueChip) ? <>{chip}{zeroRevenueChip}</> : null;

  /* Card 5134 — a disclosure that discloses nothing is noise, so the button is mounted only when
     at least one note would render. Each flag mirrors its own component's null-guard, and the
     components keep those guards: this decides MOUNTING, they decide their own content. */
  const scopeCaption = isMoney ? (metrics?.firstOrderValue ?? null) : null;
  const showPartialNote = partialCount > 0;
  const showDenominatorNote = isMoney && Boolean(integrity?.denominatorLabel);
  const showScopeCaption = (scopeCaption?.label ?? null) !== null
    || (scopeCaption?.basis ?? null) !== null
    || (scopeCaption?.termNote ?? null) !== null;
  const hasNotes = showPartialNote || showDenominatorNote || showScopeCaption || (isMobile && Boolean(chips));

  return (
    <div
      className={styles.grid}
      data-testid="executive-charts"
      data-loading={isSkeleton ? 'true' : 'false'}
      aria-busy={isSkeleton ? 'true' : 'false'}
      data-period={metrics?.period ?? ''}
      data-granularity={grain ?? ''}
      data-view={activeView}
      data-point-count={count}
      data-partial-count={partialCount}
    >
      <ChartCard
        testid="executive-chart-headline"
        skeleton={isSkeleton}
        title={isMoney ? 'Order Revenue, Cost, and Profit' : 'New Members'}
        count={count}
        comparisonWindow={comparisonWindow}
        growth={isMoney ? undefined : members.growthPct}
        population={population}
        chip={isMobile ? null : chips}
        footer={hasNotes ? (
          <ExecutiveNotesDisclosure>
            {isMobile && chips && <div className={styles.mobileNotesChips}>{chips}</div>}
            {showPartialNote && <PartialBucketNote count={partialCount} />}
            {/* Card 4310 moves this note OFF the top of the page and INTO the card that draws
                the population it names. Every word of it is still the API's own copy — the
                component passes `firstOrderValue` straight through and types none of it. */}
            {showDenominatorNote && <MarginDenominatorNote integrity={integrity} profitMargin={metrics?.profitMargin} />}
            {showScopeCaption && (
              <ExecutiveFirstOrderValueCaption
                firstOrderValue={metrics?.firstOrderValue ?? null}
                tileLabels={EXECUTIVE_KPI_DEFS.map((def) => def.label)}
              />
            )}
          </ExecutiveNotesDisclosure>
        ) : null}
      >
        <ResponsiveContainer width="100%" height="100%">
          {isMoney ? (
            <LineChart data={points} margin={{ top: 5, right: 5, left: -6, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
              {/* CARD 5338 — every number below arrives from `deriveChartAxisConfig`. Raising
                  `minTickGap` on a phone draws FEWER date labels; it is not a shrink. */}
              <XAxis dataKey="label" tick={AXIS} tickLine={false} axisLine={{ stroke: GRID }} minTickGap={axis.xMinTickGap} />
              {/* TWO UNITS, TWO AXES. Dollars left, member COUNTS right — see MEMBERS_SERIES.
                  🪤 CARD 5338: the members axis keeps its `yAxisId` at EVERY width and only stops
                  drawing TICKS. `MONEY_CHART_SERIES` still contains a `<Line yAxisId="members">`,
                  and a Line pointing at a missing axis is a recharts error, not a smaller chart. */}
              <YAxis
                yAxisId="money"
                tick={AXIS}
                tickLine={false}
                axisLine={false}
                width={axis.moneyAxisWidth}
                tickFormatter={axis.moneyTickFormat === 'compact' ? formatCompactMoney : formatMoney}
              />
              <YAxis
                yAxisId="members"
                orientation="right"
                tick={axis.membersAxisTick ? AXIS : false}
                tickLine={false}
                axisLine={false}
                width={axis.membersAxisWidth}
                tickFormatter={formatCount}
              />
              <Tooltip content={<MoneyTooltip />} />
              <Legend wrapperStyle={{ fontSize: `${axis.legendFontSize}rem` }} />
              {/* The DOM's line count is a FUNCTION of MONEY_CHART_SERIES, never a second copy. */}
              {MONEY_CHART_SERIES.map(({ dataKey, name, stroke, yAxisId }) => (
                <Line
                  key={dataKey}
                  yAxisId={yAxisId}
                  type="linear"
                  dataKey={dataKey}
                  name={name}
                  stroke={stroke}
                  strokeWidth={2}
                  dot={{ r: 2.25, strokeWidth: 1.25, fill: '#ffffff' }}
                  activeDot={{ r: 4 }}
                  strokeDasharray={yAxisId === MEMBERS_SERIES.yAxisId ? '4 3' : undefined}
                />
              ))}
            </LineChart>
          ) : (
            <LineChart data={points} margin={{ top: 5, right: 5, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
              <XAxis dataKey="label" tick={AXIS} tickLine={false} axisLine={{ stroke: GRID }} minTickGap={16} />
              <YAxis tick={AXIS} tickLine={false} axisLine={false} width={54} />
              <Tooltip formatter={countTooltip} />
              <Legend wrapperStyle={{ fontSize: '0.7rem' }} />
              <Line
                type="linear"
                dataKey="newMembers"
                name="New Members"
                stroke="#f97316"
                strokeWidth={2}
                dot={{ r: 2.25, strokeWidth: 1.25, fill: '#ffffff' }}
                activeDot={{ r: 4 }}
              />
            </LineChart>
          )}
        </ResponsiveContainer>
      </ChartCard>

      {!isMoney && (
        <ChartCard
          testid="executive-chart-total-members"
          skeleton={isSkeleton}
          title="Total Members"
          count={count}
          comparisonWindow={comparisonWindow}
          population={population}
          chip={members.excludedTotal === null ? null : (
            <span className={styles.unresolvedChip} data-testid="executive-members-excluded" data-count={members.excludedTotal}>
              {formatCount(members.excludedTotal)} excluded
            </span>
          )}
        >
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={points} margin={{ top: 5, right: 5, left: -18, bottom: 0 }}>
              <defs>
                <linearGradient id="execMembers" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.28} />
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
              <XAxis dataKey="label" tick={AXIS} tickLine={false} axisLine={{ stroke: GRID }} minTickGap={16} />
              <YAxis tick={AXIS} tickLine={false} axisLine={false} width={54} />
              <Tooltip formatter={countTooltip} />
              <Area type="linear" dataKey="totalMembers" name="Total Members" stroke="#3b82f6" strokeWidth={2} fill="url(#execMembers)" fillOpacity={0.16} dot={{ r: 2.25, strokeWidth: 1.25, fill: '#ffffff' }} activeDot={{ r: 4 }} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>
      )}
    </div>
  );
};

export default memo(ExecutiveCharts);
